import {
	cacheSuffix,
	InstantConfigGroup,
	negativePrefix,
	precision,
	samplingSeparator,
	worldWide,
} from '../../models';

export type RegionValidationPredicate = (regions: string[]) => boolean;

export interface GeoData {
	region?: string;
	country?: string;
	continent?: string;
}

export class RegionMatcher {
	constructor(private readonly geo?: GeoData) {}

	isValid(
		regions: InstantConfigGroup['regions'] = [],
		validateRegionPredicate?: RegionValidationPredicate,
	): boolean {
		const validRegions: string[] = this.filterOutInvalidRegions(regions);

		return validateRegionPredicate
			? validateRegionPredicate(validRegions)
			: this.isProperGeo(validRegions);
	}

	getCountryCode(): string | undefined {
		return this.geo?.country;
	}

	getRegionCode(): string | undefined {
		return this.geo?.region;
	}

	getContinentCode(): string | undefined {
		return this.geo?.continent;
	}

	isProperGeo(countryList: string[] = []): boolean {
		return !!(
			countryList &&
			countryList.indexOf &&
			!this.isGeoExcluded(countryList) &&
			(this.isProperContinent(countryList) ||
				this.isProperCountry(countryList) ||
				this.isProperRegion(countryList))
		);
	}

	private filterOutInvalidRegions(regions: string[]): string[] {
		return regions
			.filter((region) => !region.includes(samplingSeparator))
			.filter((region) => !region.includes(cacheSuffix));
	}

	private isProperCountry(countryList: string[]): boolean {
		return !!(
			countryList &&
			countryList.indexOf &&
			(countryList.indexOf(this.getCountryCode()) > -1 ||
				this.isSampledForGeo(countryList, this.getCountryCode()))
		);
	}

	private isProperRegion(countryList: string[]): boolean {
		const code = `${this.getCountryCode()}-${this.getRegionCode()}`;

		return !!(
			countryList &&
			countryList.indexOf &&
			(countryList.indexOf(code) > -1 || this.isSampledForGeo(countryList, code))
		);
	}

	private containsContinent(countryList: string[]): boolean {
		const geo = `${worldWide}-${this.getContinentCode()}`;

		return countryList.indexOf(geo) > -1 || this.isSampledForGeo(countryList, geo);
	}

	private containsWorldWide(countryList: string[]): boolean {
		return countryList.indexOf(worldWide) > -1 || this.isSampledForGeo(countryList, worldWide);
	}

	private isProperContinent(countryList: string[]): boolean {
		return !!(
			countryList &&
			countryList.indexOf &&
			(this.containsWorldWide(countryList) || this.containsContinent(countryList))
		);
	}

	private isGeoExcluded(countryList: string[]): boolean {
		return !!(
			countryList.indexOf(`${negativePrefix}${this.getCountryCode()}`) > -1 ||
			countryList.indexOf(`${negativePrefix}${this.getCountryCode()}-${this.getRegionCode()}`) >
				-1 ||
			countryList.indexOf(`${negativePrefix}${worldWide}-${this.getContinentCode()}`) > -1
		);
	}

	private hasSampling(geo: string): (value: string) => boolean {
		return (value: string) =>
			value.indexOf(negativePrefix) !== 0 && value.indexOf(geo + samplingSeparator) > -1;
	}

	private getSamplingLimits(value: string): number {
		let [, samplingValue]: string[] = value.split(samplingSeparator);

		samplingValue = samplingValue.replace(cacheSuffix, '');

		return Math.round(parseFloat(samplingValue) * precision) || 0;
	}

	private isSampledForGeo(countryList: string[], geo: string): boolean {
		const countryListWithSampling: string[] = countryList.filter(this.hasSampling(geo));

		if (countryListWithSampling.length === 0) {
			return false;
		}

		const randomValue: number = Math.round(Math.random() * (precision * 100)) || 0;

		return countryListWithSampling
			.map((value) => this.getSamplingLimits(value))
			.some((value) => randomValue < value);
	}
}
